import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Play, Monitor, Plus, Minus, Users, HelpCircle, History, Crown, Trash2, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0b01affe-3e55-4e38-af85-fb6ae72b034b/artifacts/aji67e46_CAKE%20FACTORY_00_LOGO_600px.png";

const RECENT_GAMES_KEY = "cake-factory-recent-games";
const MAX_RECENT_GAMES = 8;

// Save a game to recent games (used here and exported for GameMaster/Display)
export function saveRecentGame(entry) {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_GAMES_KEY) || "[]");
    const filtered = list.filter((g) => g.id !== entry.id);
    filtered.unshift({ ...entry, last_accessed: new Date().toISOString() });
    localStorage.setItem(RECENT_GAMES_KEY, JSON.stringify(filtered.slice(0, MAX_RECENT_GAMES)));
  } catch {
    /* ignore */
  }
}

export default function Home() {
  const navigate = useNavigate();
  const [numTeams, setNumTeams] = useState(1);
  const [teamNames, setTeamNames] = useState(Array(10).fill(""));
  const [creating, setCreating] = useState(false);
  const [joinGameId, setJoinGameId] = useState("");
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [recentGames, setRecentGames] = useState([]);

  // Load recent games & validate them against backend
  useEffect(() => {
    const list = JSON.parse(localStorage.getItem(RECENT_GAMES_KEY) || "[]");
    setRecentGames(list);
  }, []);

  const removeRecent = (id) => {
    const next = recentGames.filter((g) => g.id !== id);
    setRecentGames(next);
    localStorage.setItem(RECENT_GAMES_KEY, JSON.stringify(next));
  };

  const clearAllRecent = () => {
    setRecentGames([]);
    localStorage.removeItem(RECENT_GAMES_KEY);
    toast.success("Recent games cleared");
  };

  const copyId = (id) => {
    navigator.clipboard.writeText(id);
    toast.success("Game ID copied!");
  };

  const createGame = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/games`, {
        num_teams: numTeams,
        team_names: teamNames.slice(0, numTeams).filter((n) => n.trim() !== ""),
      });
      saveRecentGame({
        id: response.data.id,
        num_teams: numTeams,
        team_names: teamNames.slice(0, numTeams).filter((n) => n.trim() !== ""),
        created: true,
      });
      toast.success("Game created!");
      navigate(`/game-master/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create game");
      console.error(error);
    } finally {
      setCreating(false);
    }
  };

  const joinGame = (mode) => {
    if (!joinGameId.trim()) {
      toast.error("Please enter a Game ID");
      return;
    }
    const id = joinGameId.trim();
    saveRecentGame({ id, num_teams: null, team_names: [], created: false });
    if (mode === "master") {
      navigate(`/game-master/${id}`);
    } else {
      navigate(`/display/${id}`);
    }
    setShowJoinDialog(false);
  };

  const formatRelative = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="min-h-screen bg-[#FDF8F6] flex flex-col items-center justify-center p-6 relative">
      {/* Help icon - top right */}
      <button
        onClick={() => setShowGuideDialog(true)}
        className="absolute top-4 right-4 p-2 text-[#A21CAF] hover:text-[#D946EF] transition-colors"
        data-testid="open-guide-btn"
        title="How to play"
      >
        <HelpCircle className="w-7 h-7" />
      </button>

      {/* Logo */}
      <img
        src={LOGO_URL}
        alt="Cake Factory"
        className="w-48 h-48 object-contain mb-8 drop-shadow-lg"
        data-testid="logo"
      />

      <h1 className="font-heading text-4xl md:text-5xl text-[#A21CAF] mb-2 text-center">
        Cake Factory
      </h1>
      <p className="text-gray-600 mb-10 text-center font-medium">
        Team Building Game Manager
      </p>

      {/* Create Game Section */}
      <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-xl mb-6">
        <h2 className="font-heading text-2xl text-[#0F172A] mb-6 flex items-center gap-2">
          <Users className="w-6 h-6 text-[#D946EF]" />
          New Game
        </h2>

        {/* Team Count Selector */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Number of Teams
          </label>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setNumTeams(Math.max(1, numTeams - 1))}
              className="arcade-btn arcade-btn-penalty w-14 h-14 flex items-center justify-center text-2xl"
              data-testid="decrease-teams-btn"
            >
              <Minus className="w-6 h-6" />
            </button>
            <span className="font-mono text-4xl font-bold text-[#0F172A] w-16 text-center" data-testid="team-count">
              {numTeams}
            </span>
            <button
              onClick={() => setNumTeams(Math.min(10, numTeams + 1))}
              className="arcade-btn arcade-btn-sale w-14 h-14 flex items-center justify-center text-2xl"
              data-testid="increase-teams-btn"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Team Names (Collapsible) */}
        <details className="mb-6">
          <summary className="cursor-pointer text-sm font-semibold text-gray-700 mb-3 hover:text-[#D946EF] transition-colors">
            Customize Team Names (Optional)
          </summary>
          <div className="space-y-2 mt-3">
            {Array.from({ length: numTeams }).map((_, i) => (
              <Input
                key={i}
                placeholder={`Team ${i + 1}`}
                value={teamNames[i]}
                onChange={(e) => {
                  const newNames = [...teamNames];
                  newNames[i] = e.target.value;
                  setTeamNames(newNames);
                }}
                className="border-2 border-gray-200 focus:border-[#D946EF]"
                data-testid={`team-name-input-${i}`}
              />
            ))}
          </div>
        </details>

        {/* Create Button */}
        <button
          onClick={createGame}
          disabled={creating}
          className="arcade-btn arcade-btn-primary w-full py-4 text-xl flex items-center justify-center gap-3"
          data-testid="create-game-btn"
        >
          <Play className="w-6 h-6" />
          {creating ? "Creating..." : "Create Game"}
        </button>
      </div>

      {/* Join Game Section */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogTrigger asChild>
          <button
            className="text-[#A21CAF] font-semibold hover:underline flex items-center gap-2 mb-6"
            data-testid="join-game-trigger"
          >
            <Monitor className="w-5 h-5" />
            Join Existing Game
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Join Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Paste Game ID or paste full URL"
              value={joinGameId}
              onChange={(e) => {
                // Allow pasting a full URL like https://.../game-master/<id>, /display/<id>,
                // or /presentation?gameId=<id>
                const v = e.target.value;
                const pathMatch = v.match(/(?:game-master|display)\/([a-f0-9-]{8,})/i);
                const queryMatch = v.match(/[?&]gameId=([a-f0-9-]{8,})/i);
                setJoinGameId(pathMatch ? pathMatch[1] : queryMatch ? queryMatch[1] : v);
              }}
              className="border-2"
              data-testid="join-game-id-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => joinGame("master")}
                className="arcade-btn arcade-btn-primary flex-1 py-3 text-sm"
                data-testid="join-as-master-btn"
              >
                Game Master
              </button>
              <button
                onClick={() => joinGame("display")}
                className="arcade-btn arcade-btn-sale flex-1 py-3 text-sm"
                data-testid="join-as-display-btn"
              >
                TV Display
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Games Section */}
      {recentGames.length > 0 && (
        <div className="w-full max-w-md bg-white/60 backdrop-blur rounded-2xl p-5 shadow-md" data-testid="recent-games-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg text-[#0F172A] flex items-center gap-2">
              <History className="w-5 h-5 text-[#A21CAF]" />
              Recent Games
            </h3>
            <button
              onClick={clearAllRecent}
              className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
              data-testid="clear-recent-games-btn"
              title="Clear list"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
          <div className="space-y-2">
            {recentGames.map((g) => (
              <div
                key={g.id}
                className="flex items-center justify-between bg-white rounded-xl p-3 border border-gray-100 hover:border-[#D946EF]/40 transition-colors"
                data-testid={`recent-game-${g.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {g.created && <Crown className="w-4 h-4 text-[#F59E0B] shrink-0" title="Created by you" />}
                    <span className="font-mono text-xs text-gray-500 truncate" title={g.id}>
                      {g.id.slice(0, 8)}…{g.id.slice(-4)}
                    </span>
                    <button
                      onClick={() => copyId(g.id)}
                      className="text-gray-400 hover:text-[#D946EF]"
                      title="Copy ID"
                      data-testid={`copy-recent-${g.id}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {g.num_teams ? `${g.num_teams} team${g.num_teams > 1 ? "s" : ""} • ` : ""}
                    {formatRelative(g.last_accessed)}
                  </div>
                </div>
                <div className="flex gap-1.5 ml-2">
                  <button
                    onClick={() => {
                      saveRecentGame({ ...g, created: g.created });
                      navigate(`/game-master/${g.id}`);
                    }}
                    className="px-2.5 py-1.5 text-xs bg-[#A21CAF] text-white rounded-lg hover:bg-[#D946EF] transition-colors"
                    data-testid={`resume-master-${g.id}`}
                    title="Resume as Game Master"
                  >
                    GM
                  </button>
                  <button
                    onClick={() => {
                      saveRecentGame({ ...g, created: g.created });
                      navigate(`/display/${g.id}`);
                    }}
                    className="px-2.5 py-1.5 text-xs bg-[#22C55E] text-white rounded-lg hover:bg-[#16A34A] transition-colors"
                    data-testid={`resume-display-${g.id}`}
                    title="Open TV Display"
                  >
                    TV
                  </button>
                  <button
                    onClick={() => removeRecent(g.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500"
                    data-testid={`remove-recent-${g.id}`}
                    title="Remove from list"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guide Dialog */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-[#A21CAF]" />
              Referee Guide
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 text-sm text-gray-700">
            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">1. Create a Game</h4>
              <p>Pick number of teams (1–10), optionally name them, then tap <b>Create Game</b>. You become the <b>Lead Game Master</b> and are taken to the mobile dashboard.</p>
            </section>

            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">2. Open the TV Display</h4>
              <p>From the Game Master header, tap the <b>monitor icon</b> to open the public scoreboard in a new tab. Project it on the TV. Tap the <b>fullscreen icon</b> on the TV view for a clean projection.</p>
            </section>

            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">3. Show the Presentation</h4>
              <p>Before the game, tap the <b>presentation icon</b> in the GM header to walk participants through the rules. The <b>Start Game</b> button there switches the same screen to the live TV scoreboard.</p>
            </section>

            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">4. Add Another Referee (Multi-GM)</h4>
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>Tap the <b>share icon</b> in the GM header — show the QR code or copy the GM link.</li>
                <li>Second referee opens the link on their phone, lands on the same Game Master dashboard.</li>
                <li>They tap the <b>settings (gear) icon</b>, turn <b>Lead Game Master</b> <span className="bg-gray-100 px-1.5 rounded">OFF</span>, and pick which teams they manage.</li>
                <li>Only the Lead GM controls the round timer, round changes, and Staff Meeting. Assistants only score their assigned teams.</li>
              </ol>
            </section>

            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">5. Lost the Game ID?</h4>
              <p>Every game you create or join is saved on this device under <b>Recent Games</b> on the home screen. Bookmark the GM link or use the share dialog to keep a copy handy.</p>
            </section>

            <section>
              <h4 className="font-heading text-base text-[#0F172A] mb-1.5">6. Scoring Cheat Sheet</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><b>Cake sales:</b> 1 cake = €50 · 2 cakes = €150 · 3 cakes = €250 (+€50/extra) · 4 cakes = €400 (+€50/extra)</li>
                <li><b>Penalties:</b> −€20 or −€50</li>
                <li><b>Upgrades:</b> €70–€250 (locked permanently except Strategy/Candle/Last Call which reset each round). Buying is blocked if it would push score below €0.</li>
                <li><b>Staff Meeting:</b> 10-minute side timer. Sales & penalties disabled, upgrades enabled.</li>
              </ul>
            </section>

            <button
              onClick={() => setShowGuideDialog(false)}
              className="arcade-btn arcade-btn-primary w-full py-3"
              data-testid="close-guide-btn"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
